import { useState, FormEvent } from 'react'
import { useAuth } from '@/contexts/AuthContext'

export function LoginForm() {
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedToken = token.trim()
    if (!trimmedToken) {
      setError('Please enter a token')
      return
    }

    setIsLoading(true)
    try {
      const success = await login(trimmedToken)
      if (!success) {
        setError('Invalid or expired token')
      }
    } catch {
      setError('Failed to verify token. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-chart-bg">
      <div className="w-full max-w-md p-8 space-y-6 bg-[#1e222d] rounded-lg border border-chart-grid shadow-xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Hammer Alert System
          </h1>
          <p className="text-text-secondary text-sm">
            Enter your authentication token to access the dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="token"
              className="block text-sm font-medium text-text-secondary mb-2"
            >
              Authentication Token
            </label>
            <input
              type="text"
              id="token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your token here"
              className="w-full px-4 py-3 bg-chart-bg border border-chart-grid rounded-md
                         text-text-primary placeholder-text-secondary/50
                         focus:outline-none focus:ring-2 focus:ring-signal-bid/50 focus:border-signal-bid
                         transition-colors"
              disabled={isLoading}
              autoFocus
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="p-3 bg-signal-ask/10 border border-signal-ask/30 rounded-md">
              <p className="text-sm text-signal-ask">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !token.trim()}
            className="w-full py-3 px-4 bg-signal-bid text-white font-medium rounded-md
                       hover:bg-signal-bid/90 focus:outline-none focus:ring-2 focus:ring-signal-bid/50
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
          >
            {isLoading ? 'Verifying...' : 'Login'}
          </button>
        </form>

        <div className="pt-4 border-t border-chart-grid">
          <div className="text-xs text-text-secondary space-y-2">
            <p className="font-medium">How to get a token:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Open the Telegram bot</li>
              <li>Send the <code className="bg-chart-bg px-1 rounded">/token</code> command</li>
              <li>Copy the generated token</li>
              <li>Paste it in the field above</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )
}
